function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from "prop-types";
import { useEuiTheme } from '../../services';
import { EuiFlyoutContext } from './flyout_context';
import { jsx as ___EmotionJSX } from "@emotion/react";
/**
 * An intermediate component between EuiFlyout and EuiFlyoutChild.
 * It is responsible for managing the state of the child flyout, and passing it to EuiFlyoutContext.
 * It removes the responsibility of managing child flyout state from EuiFlyout, which is especially important there might not be a child flyout.
 */
export var EuiFlyoutChildProvider = function EuiFlyoutChildProvider(_ref) {
  var parentSize = _ref.parentSize,
    parentFlyoutRef = _ref.parentFlyoutRef,
    childElement = _ref.childElement,
    childrenToRender = _ref.childrenToRender,
    reportIsChildOpen = _ref.reportIsChildOpen,
    reportChildLayoutMode = _ref.reportChildLayoutMode;
  var _useEuiTheme = useEuiTheme(),
    euiTheme = _useEuiTheme.euiTheme;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isChildFlyoutOpen = _useState2[0],
    setIsChildFlyoutOpen = _useState2[1];
  var _useState3 = useState(typeof window !== 'undefined' ? window.innerWidth : Infinity),
    _useState4 = _slicedToArray(_useState3, 2),
    windowWidth = _useState4[0],
    setWindowWidth = _useState4[1];
  var _useState5 = useState('side-by-side'),
    _useState6 = _slicedToArray(_useState5, 2),
    childLayoutMode = _useState6[0],
    setChildLayoutMode = _useState6[1];

  // update windowWidth on resize
  useEffect(function () {
    if (typeof window === 'undefined') return;
    var handleResize = function handleResize() {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate stacking breakpoint value for child flyout.
  // Stacking breakpoint value is a sum of parent breakpoint value and child breakpoint value.
  var stackingBreakpointValue = useMemo(function () {
    var parentSizeName = parentSize;
    var childSizeName = childElement.props.size || 's';
    var parentNumericValue = 0;
    if (parentSizeName === 's') parentNumericValue = euiTheme.breakpoint.s;else if (parentSizeName === 'm') parentNumericValue = euiTheme.breakpoint.m;
    // Parent 'l' size is not allowed when child is present, so no need to check here

    var childNumericValue = 0;
    if (childSizeName === 's') childNumericValue = euiTheme.breakpoint.s;else if (childSizeName === 'm') childNumericValue = euiTheme.breakpoint.m;
    return parentNumericValue + childNumericValue;
  }, [parentSize, childElement.props.size, euiTheme.breakpoint]);

  // update childLayoutMode based on windowWidth and the calculated stackingBreakpoint
  useEffect(function () {
    if (windowWidth >= stackingBreakpointValue) {
      setChildLayoutMode('side-by-side');
    } else {
      setChildLayoutMode('stacked');
    }
  }, [windowWidth, stackingBreakpointValue]);

  // report isChildFlyoutOpen changes to the parent EuiFlyout
  useEffect(function () {
    reportIsChildOpen(isChildFlyoutOpen);
  }, [isChildFlyoutOpen, reportIsChildOpen]);

  // report childLayoutMode changes to the parent EuiFlyout
  useEffect(function () {
    reportChildLayoutMode(childLayoutMode);
  }, [childLayoutMode, reportChildLayoutMode]);
  var contextValue = useMemo(function () {
    return {
      parentSize: parentSize,
      parentFlyoutRef: parentFlyoutRef,
      isChildFlyoutOpen: isChildFlyoutOpen,
      setIsChildFlyoutOpen: setIsChildFlyoutOpen,
      childLayoutMode: childLayoutMode
    };
  }, [parentSize, parentFlyoutRef, isChildFlyoutOpen, setIsChildFlyoutOpen, childLayoutMode]);
  return ___EmotionJSX(EuiFlyoutContext.Provider, {
    value: contextValue
  }, childrenToRender);
};
EuiFlyoutChildProvider.propTypes = {
  parentSize: PropTypes.oneOf(["s", "m"]).isRequired,
  parentFlyoutRef: PropTypes.any.isRequired,
  childElement: PropTypes.element.isRequired,
  childrenToRender: PropTypes.node.isRequired,
  reportIsChildOpen: PropTypes.func.isRequired,
  reportChildLayoutMode: PropTypes.func.isRequired
};